import { PillTheme } from "@redotech/redo-model/pill-theme";
import { TagGroup } from "@redotech/redo-model/support/tag-group-zod-schema";
import { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { TextInput } from "@redotech/redo-web/text-input";
import { memo, useMemo } from "react";
import { PillColorPicker } from "./conversation-tag-input-color-picker";
import { ConversationTagPillThemes } from "./conversation-tag-pill";

export const TagInputFields = memo(function TagInputFields({
  name,
  setName,
  pillTheme,
  setPillTheme,
  showNameError,
  disableNameEdit = false,
  tagGroups = [],
  selectedTagGroup,
  setSelectedTagGroup,
}: {
  name: string;
  setName(name: string): void;
  showNameError: boolean;
  pillTheme: PillTheme;
  setPillTheme(pillTheme: PillTheme): void;
  disableNameEdit?: boolean;
  tagGroups?: TagGroup[];
  selectedTagGroup?: string | null;
  setSelectedTagGroup?: (tagGroupId: string | null) => void;
}) {
  const tagGroupOptions = useMemo((): TextRedoListItem<string | null>[] => {
    const options: TextRedoListItem<string | null>[] = [
      { id: "general", type: "text" as const, text: "General", value: null },
    ];

    for (const group of tagGroups) {
      options.push({
        id: group._id,
        type: "text" as const,
        text: group.name,
        value: group._id,
      });
    }

    return options;
  }, [tagGroups]);

  const selectedTagGroupOption = useMemo(() => {
    return tagGroupOptions.find((option) => option.value === selectedTagGroup);
  }, [tagGroupOptions, selectedTagGroup]);

  return (
    <Flex dir="column" p="sm">
      <Flex dir="column">
        <Text fontSize="md" fontWeight="semibold">
          Tag name
        </Text>
        <TextInput disabled={disableNameEdit} onChange={setName} value={name} />
        {showNameError && (
          <Text
            fontSize="xs"
            pl="sm"
            textColor="error"
          >{`A tag with the name ${name} already exists`}</Text>
        )}
      </Flex>

      {setSelectedTagGroup && (
        <Flex dir="column" pt="md">
          <Text fontSize="md" fontWeight="semibold">
            Tag group
          </Text>
          <RedoSingleSelectDropdownInput
            options={tagGroupOptions}
            optionSelected={({ item }) => {
              if (item && "type" in item && item.type === "text") {
                setSelectedTagGroup(item.value);
              }
            }}
            placeholder="Select a group"
            selectedItem={selectedTagGroupOption}
            size="sm"
          />
        </Flex>
      )}

      <Flex dir="column" pt="md">
        <Text fontSize="md" fontWeight="semibold">
          Tag color
        </Text>
        <PillColorPicker
          options={ConversationTagPillThemes}
          setValue={setPillTheme}
          value={pillTheme}
        />
      </Flex>
    </Flex>
  );
});
